// features/superAdmin/components/CampaignPageBuilder.tsx
import React, { useState, useRef } from 'react';
import type { CampaignPageData, CampaignItem } from './SuperAdminTypes';

interface CampaignPageBuilderProps {
    tenant: {
        name: string;
        logo?: string;
        primaryColor?: string;
        secondaryColor?: string;
    };
    onSave: (data: CampaignPageData, customCSS: string) => void;
    onClose: () => void;
}

const CampaignPageBuilder: React.FC<CampaignPageBuilderProps> = ({ tenant, onSave, onClose }) => {
    const [previewMode, setPreviewMode] = useState(false);
    const [activeTab, setActiveTab] = useState('header');
    const [editingCampaign, setEditingCampaign] = useState<CampaignItem | null>(null);
    const [showCampaignModal, setShowCampaignModal] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const campaignImageInputRef = useRef<HTMLInputElement>(null);

    // Add default tenant values to prevent undefined errors
    const safeTenant = {
        name: tenant?.name || 'My Brand',
        logo: tenant?.logo || 'https://via.placeholder.com/150x50',
        primaryColor: tenant?.primaryColor || '#1a1a2e',
        secondaryColor: tenant?.secondaryColor || '#e94560',
    };

    const [config, setConfig] = useState<CampaignPageData>({
        header: {
            logo: safeTenant.logo,
            logoSize: 48,
            logoPosition: 'center',
            showNavigation: false,
            navigationLinks: [],
            backgroundColor: safeTenant.primaryColor,
            textColor: '#FFFFFF',
            padding: '16',
            isSticky: false,
        },
        campaigns: {
            title: 'Our Campaigns',
            titleColor: '#FFFFFF',
            subtitle: 'Discover our latest offers and promotions',
            subtitleColor: '#E0E0E0',
            backgroundColor: '#0f3460',
            textColor: '#111827',
            layout: 'grid',
            itemsPerRow: 3,
            campaigns: [
                {
                    id: '1',
                    title: 'Summer Sale',
                    titleColor: '#111827',
                    description: 'Get up to 50% off on all summer collections',
                    descriptionColor: '#4B5563',
                    buttonText: 'Shop Now',
                    buttonTextColor: '#FFFFFF',
                    buttonBackgroundColor: safeTenant.secondaryColor,
                    buttonHoverColor: '#c73e54',
                    buttonLink: '/summer-sale',
                    image: '',
                    video: '',
                    mediaType: 'image',
                    isVisible: true,
                    order: 1,
                },
                {
                    id: '2',
                    title: 'New Year Special',
                    titleColor: '#111827',
                    description: 'Start the new year with exclusive discounts',
                    descriptionColor: '#4B5563',
                    buttonText: 'Learn More',
                    buttonTextColor: '#FFFFFF',
                    buttonBackgroundColor: safeTenant.secondaryColor,
                    buttonHoverColor: '#c73e54',
                    buttonLink: '/new-year',
                    image: '',
                    video: '',
                    mediaType: 'image',
                    isVisible: true,
                    order: 2,
                },
            ],
            isVisible: true,
        },
        footer: {
            copyright: `© 2024 ${safeTenant.name}. All rights reserved.`,
            backgroundColor: '#1f2937',
            textColor: '#9CA3AF',
            showSocialLinks: true,
            isVisible: true,
        },
    });

    const [customCSS, setCustomCSS] = useState(`
.campaigns-section {
    padding: 60px 0;
}
.campaign-card {
    transition: transform 0.3s, box-shadow 0.3s;
    cursor: pointer;
}
.campaign-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
}
    `);

    const convertToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = error => reject(error);
        });
    };

    const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file && file.type.match('image.*')) {
            const base64 = await convertToBase64(file);
            setConfig(prev => ({
                ...prev,
                header: { ...prev.header, logo: base64 }
            }));
        }
    };

    const handleCampaignMediaUpload = async (file: File, type: 'image' | 'video') => {
        if (editingCampaign) {
            const base64 = await convertToBase64(file);
            setEditingCampaign({
                ...editingCampaign,
                mediaType: type,
                [type === 'image' ? 'image' : 'video']: base64,
            });
        }
    };

    const handleDesignChange = (section: 'header' | 'campaigns' | 'footer', field: string, value: any) => {
        setConfig(prev => ({
            ...prev,
            [section]: { ...prev[section], [field]: value }
        }));
    };

    const handleCampaignChange = (campaignId: string, field: string, value: any) => {
        setConfig(prev => ({
            ...prev,
            campaigns: {
                ...prev.campaigns,
                campaigns: prev.campaigns.campaigns.map(c =>
                    c.id === campaignId ? { ...c, [field]: value } : c
                )
            }
        }));
    };

    const handleAddCampaign = () => {
        const newCampaign: CampaignItem = {
            id: Date.now().toString(),
            title: 'New Campaign',
            titleColor: '#111827',
            description: 'Campaign description goes here',
            descriptionColor: '#4B5563',
            buttonText: 'Learn More',
            buttonTextColor: '#FFFFFF',
            buttonBackgroundColor: safeTenant.secondaryColor,
            buttonHoverColor: '#c73e54',
            buttonLink: '/campaign',
            image: '',
            video: '',
            mediaType: 'image',
            isVisible: true,
            order: config.campaigns.campaigns.length + 1,
        };
        setConfig(prev => ({
            ...prev,
            campaigns: {
                ...prev.campaigns,
                campaigns: [...prev.campaigns.campaigns, newCampaign]
            }
        }));
    };

    const handleRemoveCampaign = (campaignId: string) => {
        setConfig(prev => ({
            ...prev,
            campaigns: {
                ...prev.campaigns,
                campaigns: prev.campaigns.campaigns.filter(c => c.id !== campaignId)
            }
        }));
    };

    const handleToggleCampaignVisibility = (campaignId: string) => {
        setConfig(prev => ({
            ...prev,
            campaigns: {
                ...prev.campaigns,
                campaigns: prev.campaigns.campaigns.map(c =>
                    c.id === campaignId ? { ...c, isVisible: !c.isVisible } : c
                )
            }
        }));
    };

    const handleSave = () => {
        onSave(config, customCSS);
    };

    const getLogoPositionClasses = (position: string) => {
        switch (position) {
            case 'center': return 'mx-auto';
            case 'right': return 'ml-auto';
            default: return 'mr-auto';
        }
    };

    const getLayoutClasses = () => {
        const itemsPerRow = config.campaigns.itemsPerRow;
        switch (itemsPerRow) {
            case 2: return 'md:grid-cols-2';
            case 3: return 'md:grid-cols-3';
            case 4: return 'md:grid-cols-4';
            default: return 'md:grid-cols-3';
        }
    };

    // Preview Component
    const CampaignPreview = () => (
        <div>
            <style>{customCSS}</style>
            <style>{`
                .campaign-btn:hover {
                    background-color: ${config.campaigns.campaigns[0]?.buttonHoverColor || '#c73e54'} !important;
                }
            `}</style>

            {/* Header */}
            <header style={{
                backgroundColor: config.header.backgroundColor,
                paddingTop: `${config.header.padding}px`,
                paddingBottom: `${config.header.padding}px`,
                position: config.header.isSticky ? 'sticky' : 'relative',
                top: 0,
                zIndex: 1000,
            }} className="shadow-lg">
                <div className="container mx-auto px-6">
                    <div className={`flex items-center ${getLogoPositionClasses(config.header.logoPosition)}`}>
                        {config.header.logo && (
                            <img
                                src={config.header.logo}
                                alt={safeTenant.name}
                                style={{ height: `${config.header.logoSize}px` }}
                                className="object-contain"
                            />
                        )}
                    </div>
                </div>
            </header>

            {/* Campaigns Section */}
            {config.campaigns.isVisible && (
                <section className="campaigns-section" style={{
                    backgroundColor: config.campaigns.backgroundColor,
                }}>
                    <div className="container mx-auto px-6">
                        <div className="text-center mb-12">
                            <h2 style={{
                                color: config.campaigns.titleColor,
                                fontSize: '36px',
                                fontWeight: 'bold',
                                marginBottom: '1rem',
                            }}>
                                {config.campaigns.title}
                            </h2>
                            <p style={{
                                color: config.campaigns.subtitleColor,
                                fontSize: '18px',
                            }}>
                                {config.campaigns.subtitle}
                            </p>
                        </div>

                        <div className={`grid gap-8 grid-cols-1 ${getLayoutClasses()}`}>
                            {config.campaigns.campaigns
                                .filter(c => c.isVisible)
                                .sort((a, b) => a.order - b.order)
                                .map((campaign) => (
                                    <div key={campaign.id} className="campaign-card bg-white rounded-lg overflow-hidden shadow-lg">
                                        {campaign.mediaType === 'video' && campaign.video && (
                                            <div className="h-56 overflow-hidden">
                                                <video className="w-full h-full object-cover" controls>
                                                    <source src={campaign.video} type="video/mp4" />
                                                </video>
                                            </div>
                                        )}
                                        {campaign.mediaType === 'image' && campaign.image && (
                                            <div className="h-56 overflow-hidden">
                                                <img src={campaign.image} alt={campaign.title} className="w-full h-full object-cover" />
                                            </div>
                                        )}
                                        <div className="p-6">
                                            <h3 style={{ color: campaign.titleColor }} className="text-2xl font-bold mb-3">
                                                {campaign.title}
                                            </h3>
                                            <p style={{ color: campaign.descriptionColor }} className="mb-4">
                                                {campaign.description}
                                            </p>
                                            {campaign.buttonText && (
                                                <a
                                                    href={campaign.buttonLink}
                                                    className="campaign-btn inline-block px-6 py-2 rounded-lg font-semibold transition"
                                                    style={{
                                                        backgroundColor: campaign.buttonBackgroundColor,
                                                        color: campaign.buttonTextColor,
                                                        borderRadius: '8px',
                                                    }}
                                                >
                                                    {campaign.buttonText}
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                ))}
                        </div>

                        {config.campaigns.campaigns.filter(c => c.isVisible).length === 0 && (
                            <div className="text-center py-12">
                                <p className="text-gray-500">No active campaigns at the moment. Check back soon!</p>
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* Footer */}
            {config.footer.isVisible && (
                <footer style={{
                    backgroundColor: config.footer.backgroundColor,
                    color: config.footer.textColor,
                    padding: '1.5rem 0',
                }}>
                    <div className="container mx-auto px-6 text-center">
                        <p className="text-sm">{config.footer.copyright}</p>
                    </div>
                </footer>
            )}
        </div>
    );

    // Campaign Edit Modal
    const CampaignEditModal = () => (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <h3 className="text-xl font-bold mb-4">{editingCampaign?.id ? 'Edit Campaign' : 'Add Campaign'}</h3>

                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">Title</label>
                        <input
                            type="text"
                            className="w-full p-2 border rounded"
                            value={editingCampaign?.title || ''}
                            onChange={(e) => setEditingCampaign(prev => prev ? { ...prev, title: e.target.value } : null)}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Description</label>
                        <textarea
                            className="w-full p-2 border rounded"
                            rows={3}
                            value={editingCampaign?.description || ''}
                            onChange={(e) => setEditingCampaign(prev => prev ? { ...prev, description: e.target.value } : null)}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Media Type</label>
                        <select
                            className="w-full p-2 border rounded"
                            value={editingCampaign?.mediaType || 'image'}
                            onChange={(e) => setEditingCampaign(prev => prev ? { ...prev, mediaType: e.target.value as 'image' | 'video' | 'none' } : null)}
                        >
                            <option value="image">Image</option>
                            <option value="video">Video</option>
                            <option value="none">None</option>
                        </select>
                    </div>

                    {editingCampaign?.mediaType === 'image' && (
                        <div>
                            <label className="block text-sm font-medium mb-1">Image</label>
                            <div
                                className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer"
                                onClick={() => campaignImageInputRef.current?.click()}
                            >
                                {editingCampaign.image ? (
                                    <img src={editingCampaign.image} alt="Campaign" className="h-32 mx-auto object-contain" />
                                ) : (
                                    <p className="text-sm text-gray-600">Click to upload image</p>
                                )}
                                <input
                                    ref={campaignImageInputRef}
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) handleCampaignMediaUpload(file, 'image');
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {editingCampaign?.mediaType === 'video' && (
                        <div>
                            <label className="block text-sm font-medium mb-1">Video URL</label>
                            <input
                                type="text"
                                className="w-full p-2 border rounded"
                                placeholder="Video URL or upload"
                                value={editingCampaign?.video || ''}
                                onChange={(e) => setEditingCampaign(prev => prev ? { ...prev, video: e.target.value } : null)}
                            />
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium mb-1">Button Text</label>
                        <input
                            type="text"
                            className="w-full p-2 border rounded"
                            value={editingCampaign?.buttonText || ''}
                            onChange={(e) => setEditingCampaign(prev => prev ? { ...prev, buttonText: e.target.value } : null)}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Button Link</label>
                        <input
                            type="text"
                            className="w-full p-2 border rounded"
                            value={editingCampaign?.buttonLink || ''}
                            onChange={(e) => setEditingCampaign(prev => prev ? { ...prev, buttonLink: e.target.value } : null)}
                        />
                    </div>
                </div>

                <div className="flex justify-end gap-2 mt-6">
                    <button
                        onClick={() => setShowCampaignModal(false)}
                        className="px-4 py-2 bg-gray-500 text-white rounded"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => {
                            if (editingCampaign) {
                                if (editingCampaign.id) {
                                    handleCampaignChange(editingCampaign.id, 'title', editingCampaign.title);
                                    handleCampaignChange(editingCampaign.id, 'description', editingCampaign.description);
                                    handleCampaignChange(editingCampaign.id, 'mediaType', editingCampaign.mediaType);
                                    handleCampaignChange(editingCampaign.id, 'image', editingCampaign.image);
                                    handleCampaignChange(editingCampaign.id, 'video', editingCampaign.video);
                                    handleCampaignChange(editingCampaign.id, 'buttonText', editingCampaign.buttonText);
                                    handleCampaignChange(editingCampaign.id, 'buttonLink', editingCampaign.buttonLink);
                                } else {
                                    setConfig(prev => ({
                                        ...prev,
                                        campaigns: {
                                            ...prev.campaigns,
                                            campaigns: [...prev.campaigns.campaigns, { ...editingCampaign, id: Date.now().toString() }]
                                        }
                                    }));
                                }
                            }
                            setShowCampaignModal(false);
                            setEditingCampaign(null);
                        }}
                        className="px-4 py-2 bg-blue-500 text-white rounded"
                    >
                        Save
                    </button>
                </div>
            </div>
        </div>
    );

    // Add loading state if tenant is undefined
    if (!tenant) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                <div className="bg-white rounded-lg p-8">
                    <p className="text-gray-600">Loading campaign builder...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-hidden">
            <div className="h-full flex flex-col">
                {/* Header */}
                <div className="bg-white shadow-md p-4 flex justify-between items-center">
                    <h2 className="text-2xl font-bold">Campaign Page Builder - {safeTenant.name}</h2>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setPreviewMode(!previewMode)}
                            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
                        >
                            {previewMode ? 'Edit Mode' : 'Preview Mode'}
                        </button>
                        <button onClick={handleSave} className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">
                            Save & Publish
                        </button>
                        <button onClick={onClose} className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600">
                            Close
                        </button>
                    </div>
                </div>

                {/* Main Content */}
                <div className="flex-1 flex overflow-hidden">
                    {!previewMode && (
                        <div className="w-96 bg-gray-100 overflow-y-auto p-4 border-r">
                            <div className="space-y-4">
                                {/* Page Tabs */}
                                <div className="flex gap-2 border-b pb-2">
                                    <button
                                        onClick={() => setActiveTab('header')}
                                        className={`px-3 py-1 rounded text-sm ${activeTab === 'header' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
                                    >
                                        Header
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('campaigns')}
                                        className={`px-3 py-1 rounded text-sm ${activeTab === 'campaigns' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
                                    >
                                        Campaigns
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('footer')}
                                        className={`px-3 py-1 rounded text-sm ${activeTab === 'footer' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
                                    >
                                        Footer
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('css')}
                                        className={`px-3 py-1 rounded text-sm ${activeTab === 'css' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
                                    >
                                        Custom CSS
                                    </button>
                                </div>

                                {activeTab === 'header' && (
                                    <div className="space-y-4">
                                        {/* Logo Upload */}
                                        <div className="bg-white p-4 rounded shadow">
                                            <h3 className="font-bold mb-3">Logo Settings</h3>
                                            <div
                                                className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer"
                                                onClick={() => fileInputRef.current?.click()}
                                            >
                                                {config.header.logo ? (
                                                    <img src={config.header.logo} alt="Logo" className="h-16 mx-auto object-contain" />
                                                ) : (
                                                    <p className="text-sm text-gray-600">Click to upload logo</p>
                                                )}
                                                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                                            </div>

                                            <div className="mt-3">
                                                <label className="block text-sm mb-1">Logo Size (px)</label>
                                                <input
                                                    type="range"
                                                    min="24"
                                                    max="120"
                                                    step="4"
                                                    className="w-full"
                                                    value={config.header.logoSize}
                                                    onChange={(e) => handleDesignChange('header', 'logoSize', parseInt(e.target.value))}
                                                />
                                                <div className="text-center text-sm">{config.header.logoSize}px</div>
                                            </div>

                                            <div className="mt-3">
                                                <label className="block text-sm mb-1">Logo Position</label>
                                                <select
                                                    className="w-full p-2 border rounded"
                                                    value={config.header.logoPosition}
                                                    onChange={(e) => handleDesignChange('header', 'logoPosition', e.target.value)}
                                                >
                                                    <option value="left">Left</option>
                                                    <option value="center">Center</option>
                                                    <option value="right">Right</option>
                                                </select>
                                            </div>

                                            <div className="mt-3">
                                                <label className="block text-sm mb-1">Background Color</label>
                                                <input
                                                    type="color"
                                                    className="w-full h-10 border rounded"
                                                    value={config.header.backgroundColor}
                                                    onChange={(e) => handleDesignChange('header', 'backgroundColor', e.target.value)}
                                                />
                                            </div>

                                            <div className="mt-3">
                                                <label className="block text-sm mb-1">Header Padding (px)</label>
                                                <input
                                                    type="range"
                                                    min="8"
                                                    max="32"
                                                    step="4"
                                                    className="w-full"
                                                    value={parseInt(config.header.padding)}
                                                    onChange={(e) => handleDesignChange('header', 'padding', e.target.value)}
                                                />
                                                <div className="text-center text-sm">{config.header.padding}px</div>
                                            </div>

                                            <div className="mt-3 flex items-center justify-between">
                                                <label className="text-sm">Sticky Header</label>
                                                <button
                                                    onClick={() => handleDesignChange('header', 'isSticky', !config.header.isSticky)}
                                                    className={`px-3 py-1 rounded text-sm ${config.header.isSticky ? 'bg-green-500 text-white' : 'bg-gray-300'}`}
                                                >
                                                    {config.header.isSticky ? 'Yes' : 'No'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'campaigns' && (
                                    <div className="space-y-4">
                                        {/* Campaign Section Settings */}
                                        <div className="bg-white p-4 rounded shadow">
                                            <h3 className="font-bold mb-3">Campaign Section Settings</h3>
                                            <div className="space-y-3">
                                                <div>
                                                    <label className="block text-sm mb-1">Section Title</label>
                                                    <input
                                                        type="text"
                                                        className="w-full p-2 border rounded"
                                                        value={config.campaigns.title}
                                                        onChange={(e) => handleDesignChange('campaigns', 'title', e.target.value)}
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm mb-1">Title Color</label>
                                                    <input
                                                        type="color"
                                                        className="w-full h-10 border rounded"
                                                        value={config.campaigns.titleColor}
                                                        onChange={(e) => handleDesignChange('campaigns', 'titleColor', e.target.value)}
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm mb-1">Subtitle</label>
                                                    <input
                                                        type="text"
                                                        className="w-full p-2 border rounded"
                                                        value={config.campaigns.subtitle}
                                                        onChange={(e) => handleDesignChange('campaigns', 'subtitle', e.target.value)}
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm mb-1">Subtitle Color</label>
                                                    <input
                                                        type="color"
                                                        className="w-full h-10 border rounded"
                                                        value={config.campaigns.subtitleColor}
                                                        onChange={(e) => handleDesignChange('campaigns', 'subtitleColor', e.target.value)}
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm mb-1">Background Color</label>
                                                    <input
                                                        type="color"
                                                        className="w-full h-10 border rounded"
                                                        value={config.campaigns.backgroundColor}
                                                        onChange={(e) => handleDesignChange('campaigns', 'backgroundColor', e.target.value)}
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm mb-1">Layout</label>
                                                    <select
                                                        className="w-full p-2 border rounded"
                                                        value={config.campaigns.layout}
                                                        onChange={(e) => handleDesignChange('campaigns', 'layout', e.target.value)}
                                                    >
                                                        <option value="grid">Grid Layout</option>
                                                        <option value="list">List Layout</option>
                                                        <option value="carousel">Carousel Layout</option>
                                                    </select>
                                                </div>

                                                {config.campaigns.layout === 'grid' && (
                                                    <div>
                                                        <label className="block text-sm mb-1">Items Per Row</label>
                                                        <select
                                                            className="w-full p-2 border rounded"
                                                            value={config.campaigns.itemsPerRow}
                                                            onChange={(e) => handleDesignChange('campaigns', 'itemsPerRow', parseInt(e.target.value))}
                                                        >
                                                            <option value="2">2 per row</option>
                                                            <option value="3">3 per row</option>
                                                            <option value="4">4 per row</option>
                                                        </select>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Campaign Management */}
                                        <div className="bg-white p-4 rounded shadow">
                                            <div className="flex justify-between items-center mb-3">
                                                <h3 className="font-bold">Manage Campaigns</h3>
                                                <button
                                                    onClick={() => {
                                                        setEditingCampaign({
                                                            id: '',
                                                            title: 'New Campaign',
                                                            titleColor: '#111827',
                                                            description: 'Campaign description',
                                                            descriptionColor: '#4B5563',
                                                            buttonText: 'Learn More',
                                                            buttonTextColor: '#FFFFFF',
                                                            buttonBackgroundColor: safeTenant.secondaryColor,
                                                            buttonHoverColor: '#c73e54',
                                                            buttonLink: '/campaign',
                                                            image: '',
                                                            video: '',
                                                            mediaType: 'image',
                                                            isVisible: true,
                                                            order: config.campaigns.campaigns.length + 1,
                                                        });
                                                        setShowCampaignModal(true);
                                                    }}
                                                    className="px-3 py-1 bg-blue-500 text-white text-sm rounded"
                                                >
                                                    + Add Campaign
                                                </button>
                                            </div>

                                            <div className="space-y-2 max-h-96 overflow-y-auto">
                                                {config.campaigns.campaigns.map((campaign) => (
                                                    <div key={campaign.id} className="border rounded-lg p-3">
                                                        <div className="flex items-center justify-between gap-2 mb-2">
                                                            <div className="flex items-center gap-2 flex-1">
                                                                <button
                                                                    onClick={() => handleToggleCampaignVisibility(campaign.id)}
                                                                    className={`px-2 py-0.5 text-xs rounded ${campaign.isVisible ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}
                                                                >
                                                                    {campaign.isVisible ? '👁️' : '👁️‍🗨️'}
                                                                </button>
                                                                <span className="font-semibold text-sm truncate">{campaign.title}</span>
                                                                <span className="text-xs text-gray-500">#{campaign.order}</span>
                                                            </div>
                                                            <div className="flex gap-1">
                                                                <button
                                                                    onClick={() => {
                                                                        setEditingCampaign(campaign);
                                                                        setShowCampaignModal(true);
                                                                    }}
                                                                    className="p-1 bg-blue-500 text-white rounded text-xs"
                                                                >
                                                                    Edit
                                                                </button>
                                                                <button
                                                                    onClick={() => handleRemoveCampaign(campaign.id)}
                                                                    className="p-1 bg-red-500 text-white rounded text-xs"
                                                                >
                                                                    Delete
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <p className="text-xs text-gray-600 line-clamp-2">{campaign.description}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'footer' && (
                                    <div className="bg-white p-4 rounded shadow">
                                        <h3 className="font-bold mb-3">Footer Settings</h3>
                                        <div className="space-y-3">
                                            <div className="flex items-center justify-between">
                                                <label className="text-sm">Show Footer</label>
                                                <button
                                                    onClick={() => handleDesignChange('footer', 'isVisible', !config.footer.isVisible)}
                                                    className={`px-3 py-1 rounded text-sm ${config.footer.isVisible ? 'bg-green-500 text-white' : 'bg-gray-300'}`}
                                                >
                                                    {config.footer.isVisible ? 'Visible' : 'Hidden'}
                                                </button>
                                            </div>

                                            <div>
                                                <label className="block text-sm mb-1">Copyright Text</label>
                                                <input
                                                    type="text"
                                                    className="w-full p-2 border rounded"
                                                    value={config.footer.copyright}
                                                    onChange={(e) => handleDesignChange('footer', 'copyright', e.target.value)}
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm mb-1">Background Color</label>
                                                <input
                                                    type="color"
                                                    className="w-full h-10 border rounded"
                                                    value={config.footer.backgroundColor}
                                                    onChange={(e) => handleDesignChange('footer', 'backgroundColor', e.target.value)}
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm mb-1">Text Color</label>
                                                <input
                                                    type="color"
                                                    className="w-full h-10 border rounded"
                                                    value={config.footer.textColor}
                                                    onChange={(e) => handleDesignChange('footer', 'textColor', e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'css' && (
                                    <div className="bg-white p-4 rounded shadow">
                                        <h3 className="font-bold mb-3">Custom CSS</h3>
                                        <textarea
                                            className="w-full h-96 p-2 border rounded font-mono text-sm"
                                            value={customCSS}
                                            onChange={(e) => setCustomCSS(e.target.value)}
                                            placeholder="/* Add your custom CSS here */"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Preview Area */}
                    <div className={`flex-1 overflow-y-auto ${previewMode ? '' : 'border-l'}`}>
                        <CampaignPreview />
                    </div>
                </div>
            </div>

            {/* Campaign Edit Modal */}
            {showCampaignModal && <CampaignEditModal />}
        </div>
    );
};

export default CampaignPageBuilder;
